// src/DoctorStartVideoConsultation.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorStartVideoConsultation.css";

const DoctorStartVideoConsultation = () => {
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [acceptedAppointments, setAcceptedAppointments] = useState([]);
  const [rescheduledAppointments, setRescheduledAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768); // Responsive sidebar

  useEffect(() => {
    const fetchAppointments = async () => {
      setIsLoading(true);
      if (!doctorUser || doctorUser.role !== "doctor") {
        console.error("DoctorStartVideoConsultation - No doctorUser or incorrect role.");
        setError("You must be logged in as a doctor to view consultations.");
        setTimeout(() => navigate('/doctor-login'), 2000);
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/appointments/doctor/${doctorUser.id}`, {
          headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
        });
        console.log("DoctorStartVideoConsultation - Fetched appointments:", response.data);

        const appointments = response.data;
        const now = new Date();

        setAcceptedAppointments(
          appointments.filter((app) => app.status.toLowerCase() === "accepted" && new Date(app.date) >= now)
        );

        setRescheduledAppointments(
          appointments.filter((app) => {
            const effectiveDate = app.rescheduledDate ? new Date(app.rescheduledDate) : new Date(app.date);
            return app.status.toLowerCase() === "rescheduled" && effectiveDate >= now;
          })
        );
      } catch (error) {
        console.error("DoctorStartVideoConsultation - Error fetching appointments:", error.response?.data || error.message);
        setError(`Failed to load appointments: ${error.response?.data?.message || error.response?.data?.error || error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointments();
  }, [doctorUser, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startVideoConsultation = (appointment) => {
    console.log("DoctorStartVideoConsultation - Navigating to video call with appointment ID:", appointment._id);
    navigate(`/doctor/video-call/${appointment._id}`);
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  if (isLoading) {
    return (
      <div className="doctor-video-consultation-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="consultation-content">
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading your TureMedicine consultations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="doctor-video-consultation-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="consultation-content">
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <div className="error-message">
            <h1>Error</h1>
            <p>{error}</p>
            <div className="action-buttons">
              <button onClick={() => navigate("/doctor-login")}>Go to Login</button>
              <button onClick={() => navigate("/doctor-dashboard")}>Back to Dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-video-consultation-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="consultation-content">
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <h1>Start Video Consultation</h1>

        <div className="appointments-grid">
          <div className="appointment-section">
            <h2>Accepted Appointments</h2>
            {acceptedAppointments.length > 0 ? (
              acceptedAppointments.map((app) => (
                <div key={app._id} className="appointment-card">
                  <p><strong>Patient:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms}</p>
                  <p><strong>Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  <button
                    className="start-button"
                    onClick={() => startVideoConsultation(app)}
                  >
                    Start Consultation
                  </button>
                </div>
              ))
            ) : (
              <p>No upcoming accepted appointments available.</p>
            )}
          </div>

          <div className="appointment-section">
            <h2>Upcoming Rescheduled Appointments</h2>
            {rescheduledAppointments.length > 0 ? (
              rescheduledAppointments.map((app) => (
                <div key={app._id} className="appointment-card">
                  <p><strong>Patient:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms}</p>
                  <p><strong>Original Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  {app.rescheduledDate && (
                    <p><strong>Rescheduled Date:</strong> {new Date(app.rescheduledDate).toLocaleString()}</p>
                  )}
                  <button
                    className="start-button"
                    onClick={() => startVideoConsultation(app)}
                  >
                    Start Consultation
                  </button>
                </div>
              ))
            ) : (
              <p>No upcoming rescheduled appointments available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorStartVideoConsultation;